import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, put, patch, del } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { DEFAULT_LIST_PAGE_SIZE } from "../../Utility/Pagination/listPaginationConstants";

/**
 * Fetch teams data with optional filters and pagination
 * GET /teams?page=1&limit=10&name=Engineering&status=ACTIVE
 * Returns raw API response: { success, data: { data: [teams], meta } }
 */
export const fetchTeamsData = createAsyncThunk(
  "teams/fetchData",
  async (params = {}, { getState, rejectWithValue }) => {
    const getAll = params.getAll === true;
    if (getAll && !params.force) {
      const { allListLoaded, allListData } = getState().teams;
      if (allListLoaded && allListData != null) {
        return allListData;
      }
    }
    try {
      const endpoint = getAll ? API_ENDPOINTS.TEAMS.GET_ALL_LIST : API_ENDPOINTS.TEAMS.GET_ALL;

      const query = getAll
        ? {}
        : (() => {
            const page = params.page ?? 1;
            const limit = params.limit ?? DEFAULT_LIST_PAGE_SIZE;
            const q = { page, limit };
            if (params.name != null && String(params.name).trim() !== "") {
              q.name = String(params.name).trim();
            }
            if (params.status != null && String(params.status).trim() !== "") {
              q.status = String(params.status).trim();
            }
            return q;
          })();

      const response = await get(endpoint, query);
      const raw = response.data;
      // Normalize: support both { data: { data, meta } } and { data: [...], meta }
      if (raw?.data != null && Array.isArray(raw.data) && raw.data?.data === undefined) {
        return { data: { data: raw.data, meta: raw.meta ?? null } };
      }
      return raw;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch teams data"));
    }
  }
);

/**
 * Fetch team by ID
 */
export const fetchTeamById = createAsyncThunk(
  "teams/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.TEAMS.GET_BY_ID(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch team data"));
    }
  }
);

/**
 * Create a new team
 */
export const createTeam = createAsyncThunk(
  "teams/create",
  async (teamData, { rejectWithValue }) => {
    try {
      const response = await post(API_ENDPOINTS.TEAMS.CREATE, teamData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create team"));
    }
  }
);

/**
 * Update an existing team
 */
export const updateTeam = createAsyncThunk(
  "teams/update",
  async ({ id, teamData }, { rejectWithValue }) => {
    try {
      const response = await put(API_ENDPOINTS.TEAMS.UPDATE(id), teamData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update team"));
    }
  }
);

/**
 * Fetch users in a team
 * GET /teams/:teamId/users
 */
export const fetchTeamUsers = createAsyncThunk(
  "teams/fetchUsers",
  async (teamId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.TEAMS.GET_USERS(teamId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch team users"));
    }
  }
);

/**
 * Add team member by email
 * POST /teams/:teamId/members { email }
 */
export const addTeamMember = createAsyncThunk(
  "teams/addMember",
  async ({ teamId, email }, { rejectWithValue }) => {
    try {
      const body = {
        email: (email || "").trim(),
      };
      const response = await post(API_ENDPOINTS.TEAMS.ADD_MEMBER(teamId), body, {}, "json", {
        suppressGlobalErrorModal: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to add team member"));
    }
  }
);

/**
 * Update team member (e.g. role / job title)
 * PATCH /teams/:teamId/members/:userId
 */
export const updateTeamMember = createAsyncThunk(
  "teams/updateMember",
  async ({ teamId, userId, payload }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.TEAMS.TEAM_MEMBER(teamId, userId);
      const response = await patch(endpoint, payload, {}, {}, { suppressGlobalErrorModal: true });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update team member"));
    }
  }
);

/**
 * Remove user from team (clears team on user)
 * DELETE /teams/:teamId/members/:userId
 */
export const removeTeamMemberFromTeam = createAsyncThunk(
  "teams/removeMember",
  async ({ teamId, userId }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.TEAMS.TEAM_MEMBER(teamId, userId);
      await del(endpoint);
      return { teamId, userId };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to remove team member"));
    }
  }
);

/**
 * Delete a team
 */
export const deleteTeam = createAsyncThunk("teams/delete", async (id, { rejectWithValue }) => {
  try {
    await del(API_ENDPOINTS.TEAMS.DELETE(id));
    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error, "Failed to delete team"));
  }
});
